import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { 
  FileText, 
  AlertTriangle, 
  ChevronRight, 
  CheckCircle,
  Clock,
  User,
  Bell
} from 'lucide-react';

/**
 * PendingFormsAlert Component
 * Displays alerts for unsigned/pending required forms
 * Used in both Parent Portal and Therapist/Admin views
 */
const PendingFormsAlert = ({ 
  apiCall, 
  token, 
  clientId = null,  // If provided, shows forms for specific client
  viewType = 'therapist',  // 'therapist' | 'parent' | 'admin'
  onFormClick = null,  // Callback when a form is clicked
  onClientClick = null,  // Callback when a client is clicked (therapist view)
  compact = false  // Compact mode for dashboard widgets
}) => {
  const [loading, setLoading] = useState(true);
  const [pendingData, setPendingData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPendingForms = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let response;
        
        if (clientId && viewType === 'parent') {
          // Parent view - use parent portal endpoint for specific child
          response = await apiCall(`/parent/forms/pending/${clientId}`, 'GET', null, token);
        } else if (clientId) {
          // Therapist/admin view for specific client
          response = await apiCall(`/clients/${clientId}/pending-required-forms`, 'GET', null, token);
        } else if (viewType === 'therapist' || viewType === 'admin') {
          // Get all clients with pending forms for therapist/admin
          response = await apiCall('/therapist/clients-pending-forms', 'GET', null, token);
        } else {
          // Parent view summary - use parent portal endpoint
          response = await apiCall('/forms/alerts/summary', 'GET', null, token);
        }
        
        // Handle nested data structure from apiCall wrapper
        const responseData = response?.data?.data || response?.data || response;
        
        if (response.success && responseData) {
          // For parent/forms/pending endpoint, wrap data in expected format
          if (clientId && viewType === 'parent' && Array.isArray(responseData)) {
            setPendingData({
              data: responseData,
              pending_count: responseData.length,
              total_required: responseData.length,
              completed_count: 0
            });
          } else {
            setPendingData(responseData);
          }
        } else {
          setPendingData(null);
        }
      } catch (err) {
        console.error('Error loading pending forms:', err);
        setError('Failed to load form status');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPendingForms();
  }, [clientId, viewType, apiCall, token]);

  if (loading) {
    return (
      <Card className="border-orange-200 bg-orange-50/50">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-orange-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600" />
            <span className="text-sm">Loading form status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return null; // Silent fail for non-critical alert
  }

  // Client-specific view (for parent portal or when viewing a specific client)
  if (clientId && pendingData) {
    const { data: forms, pending_count, total_required, completed_count } = pendingData;
    
    if (!forms || forms.length === 0) {
      return (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">All Required Forms Completed</p>
                <p className="text-sm text-green-600">
                  {completed_count || 0} of {total_required || 0} forms signed
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className="border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50" data-testid="pending-forms-alert">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <AlertTriangle className="w-5 h-5" />
            <span>{pending_count} Form{pending_count > 1 ? 's' : ''} Require{pending_count === 1 ? 's' : ''} Signature</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-orange-700">
            The following forms need to be completed and signed:
          </p>
          
          <div className="space-y-2">
            {forms.map((form) => (
              <div 
                key={form.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200 hover:border-orange-400 transition-colors cursor-pointer"
                onClick={() => onFormClick?.(form)}
                data-testid={`pending-form-${form.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center">
                    <FileText className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{form.name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {form.requires_parent_signature && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Parent Signature Required</span>
                      )}
                      {form.is_required_for_onboarding && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded">Required for Onboarding</span>
                      )}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            ))}
          </div>
          
          <div className="flex items-center justify-between pt-2 border-t border-orange-200">
            <span className="text-sm text-orange-600">
              {completed_count || 0} of {total_required || 0} completed
            </span>
            <div className="w-32 h-2 bg-orange-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 transition-all" 
                style={{ width: `${total_required ? ((completed_count || 0) / total_required) * 100 : 0}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Therapist/Admin view - show all clients with pending forms
  if ((viewType === 'therapist' || viewType === 'admin') && pendingData) {
    const { data: clientsWithPending, total_clients_with_pending, total_pending_forms } = pendingData;
    
    if (!clientsWithPending || clientsWithPending.length === 0) {
      if (compact) return null;
      
      return (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">All Youth Intake Forms Complete</p>
                <p className="text-sm text-green-600">No pending consent or intake forms for any youths. (Session-level signatures are tracked separately in the Readiness Queue.)</p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (compact) {
      return (
        <Alert className="border-orange-300 bg-orange-50" data-testid="pending-forms-alert-compact">
          <AlertTriangle className="w-4 h-4 text-orange-600" />
          <AlertDescription className="text-orange-800">
            <strong>{total_clients_with_pending} youth{total_clients_with_pending > 1 ? 's' : ''}</strong> with{' '}
            <strong>{total_pending_forms} unsigned form{total_pending_forms > 1 ? 's' : ''}</strong>
          </AlertDescription>
        </Alert>
      );
    }

    return (
      <Card className="border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50" data-testid="pending-forms-alert-therapist">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-orange-800">
              <Bell className="w-5 h-5" />
              <span>Youths with Pending Forms</span>
            </div>
            <span className="text-sm font-normal text-orange-600 bg-orange-100 px-3 py-1 rounded-full">
              {total_pending_forms} total
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-orange-700">
            These youths have required forms that need signatures:
          </p>
          
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {clientsWithPending.map((client) => (
              <div 
                key={client.client_id}
                className="p-3 bg-white rounded-lg border border-orange-200 hover:border-orange-400 transition-colors cursor-pointer"
                onClick={() => onClientClick?.(client.client_id)}
                data-testid={`client-pending-${client.client_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{client.client_name}</p>
                      <p className="text-xs text-gray-500">
                        Guardian: {client.guardian_name || 'Not specified'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-orange-600">
                      <Clock className="w-4 h-4" />
                      <span className="font-semibold">{client.pending_count}</span>
                      <span className="text-sm">pending</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {client.completed_count}/{client.total_required} complete
                    </p>
                  </div>
                </div>
                
                {/* Show pending form names */}
                <div className="mt-2 flex flex-wrap gap-1">
                  {client.pending_forms.slice(0, 3).map((form) => (
                    <span 
                      key={form.id}
                      className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded"
                    >
                      {form.name}
                    </span>
                  ))}
                  {client.pending_forms.length > 3 && (
                    <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                      +{client.pending_forms.length - 3} more
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Summary view (dashboard alert)
  if (pendingData?.data) {
    const { clients_with_pending_forms, total_pending_forms, alerts } = pendingData.data;
    
    if (clients_with_pending_forms === 0) {
      if (compact) return null;
      
      return (
        <Card className="border-green-200 bg-green-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800">All required forms are signed</span>
            </div>
          </CardContent>
        </Card>
      );
    }

    return (
      <Alert className="border-orange-300 bg-orange-50" data-testid="pending-forms-summary">
        <AlertTriangle className="w-4 h-4 text-orange-600" />
        <AlertDescription className="text-orange-800">
          <strong>{clients_with_pending_forms} youth{clients_with_pending_forms > 1 ? 's' : ''}</strong> with{' '}
          <strong>{total_pending_forms} unsigned form{total_pending_forms > 1 ? 's' : ''}</strong>
        </AlertDescription>
      </Alert>
    );
  }

  return null;
};

export default PendingFormsAlert;
